import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import {
  selectSettings,
  selectIsLoading,
  selectShippingMethods,
} from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { CheckoutSettingsConfig } from '@pages/checkout-app/checkout-layout/main/components/settings/settings.models';
import { Subject, Observable } from 'rxjs';
import { CheckoutSettings } from '@pdqdev/pdq-shared/dist/entities';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { takeUntil } from 'rxjs/operators';
import { SettingsDTO } from '@pages/checkout-app/checkout-layout/main/components/settings/settings.dto';
import { CheckoutActions } from '@pages/checkout-app/checkout-store/actions';
import { ofType, Actions } from '@ngrx/effects';
import { ToastService } from '@storybook/services/toast/toast.service';
import { ShopService } from '@services/shops/shop.service';
import { Shop } from '@services/shops/shop.model';
import { MatDialog } from '@angular/material/dialog';
import { AbtestSettingsChangePercentDialogComponent } from '@pages/checkout-app/checkout-layout/main/components/settings/components/abtest-settings/change-percent-dialog/abtest-settings-change-percent-dialog.component';

const APP_REQUIREMENTS_SCOPE_TO_DISPLAY_TEXT_MAP = {
  write_delivery_customizations:
    'Your current app permissions do not allow for delivery customization. Please update your app to enable the necessary permissions and access this feature.',
};

@Component({
  selector: 'checkout-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss', '../../../../../settings/settings.scss'],
  standalone: false,
})
export class SettingsComponent implements OnInit, OnDestroy {
  @onDestroyComplete() destroy$ = new Subject();
  shippings$ = this.store.select(selectShippingMethods);
  settings$ = this.store.select(selectSettings);
  loading$ = this.store.select(selectIsLoading);
  settingsConfig = CheckoutSettingsConfig;
  newAbTestingAndCheckoutIsEnabled: boolean = false;

  formGroup: UntypedFormGroup;
  changesCounter: number = 0;
  loadedSettingsValues;
  actionCreated: boolean = false;
  shop$: Observable<Shop>;

  // required scopes
  appRequirements: { updateRequired: boolean; updateAppUrl: string };
  appRequirementsList: string[] = [];
  appRequiredScopes = ['read_delivery_customizations', 'write_delivery_customizations'];

  constructor(
    private store: Store<fromCheckoutReducer.State>,
    private formBuilder: UntypedFormBuilder,
    private actions$: Actions,
    private toastService: ToastService,
    private shopService: ShopService,
    private abTestChangesDialog: MatDialog,
  ) {}

  ngOnDestroy() {}

  ngOnInit() {
    this.store.dispatch(CheckoutActions.loadCheckouts());
    this.store.dispatch(CheckoutActions.loadCheckoutSettings());
    this.shop$ = this.shopService.shop$;
    this.detectSuccess();
    this.detectFailure();
    this.settings$.pipe(takeUntil(this.destroy$)).subscribe((settings) => {
      this.initForm(settings);
      this.loadedSettingsValues = settings;
    });
    this.shop$.pipe(takeUntil(this.destroy$)).subscribe((shop) => {
      this.newAbTestingAndCheckoutIsEnabled =
        (shop?.metadata?.useNewCheckout && shop?.metadata?.enableNewAbTesting) || false;
    });
    this.initAppRequirements();
  }

  detectSuccess() {
    this.actions$
      .pipe(
        takeUntil(this.destroy$),
        ofType(CheckoutActions.saveCheckoutSettingsSuccess, CheckoutActions.editCheckoutSettingsSuccess),
      )
      .subscribe(() => {
        this.toastService.showToast({ mode: 'info', message: 'Settings saved successfully' });
        this.store.dispatch(CheckoutActions.loadCheckoutSettings());
      });
  }

  detectFailure() {
    this.actions$
      .pipe(
        takeUntil(this.destroy$),
        ofType(CheckoutActions.saveCheckoutSettingsFailure, CheckoutActions.editCheckoutSettingsFailure),
      )
      .subscribe(() => this.toastService.showToast({ mode: 'danger', message: 'Failed to save settings' }));
  }

  initForm(loadedSettings: CheckoutSettings) {
    this.formGroup = this.formBuilder.group(this.initFields(loadedSettings));

    this.formGroup.valueChanges.subscribe((formValues) => {
      let displayFooter: boolean = false;
      this.changesCounter = 0;

      this.settingsConfig.forEach((field) => {
        if (!field.skipCounterChanges) {
          const loadedFieldValue = loadedSettings && loadedSettings[field.fieldName];
          const currentValue = formValues[field.fieldName];
          const originalValue = loadedFieldValue ?? field.defaultValue;
          const hasChanged = Array.isArray(currentValue)
            ? JSON.stringify(currentValue) !== JSON.stringify(originalValue)
            : currentValue != originalValue;
          if (hasChanged) {
            this.changesCounter++;
            displayFooter = true;
          }
        }
      });

      if (!displayFooter) {
        this.formGroup.markAsPristine();
      }
    });
  }

  initFields(loadedSettings: CheckoutSettings) {
    const fields = {};
    this.settingsConfig.forEach((field) => {
      const loadedFieldValue = loadedSettings && loadedSettings[field.fieldName];
      fields[field.fieldName] = [loadedFieldValue ?? field.defaultValue];
    });
    return fields;
  }

  onCancel() {
    this.initForm(this.loadedSettingsValues ?? undefined);
    this.formGroup.markAsPristine();
  }

  save(settings) {
    if (this.formGroup.controls['isTestMode'].dirty) {
      settings.isTestMode = this.formGroup.value.isTestMode;
    }

    if (this.loadedSettingsValues?.id) {
      settings.id = this.loadedSettingsValues.id;
      this.store.dispatch(CheckoutActions.editCheckoutSettings({ settings }));
    } else {
      this.store.dispatch(CheckoutActions.saveCheckoutSettings({ settings }));
    }
  }

  onSave() {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) return;

    this.actionCreated = true;
    const settings = new SettingsDTO(this.formGroup.value, this.loadedSettingsValues).buildSettingsDTO();

    if (settings.percentTestVariantChanged) {
      const abTestChangesRef = this.abTestChangesDialog.open(AbtestSettingsChangePercentDialogComponent);
      abTestChangesRef.componentInstance.onSave.subscribe(() => {
        this.save(settings);
        abTestChangesRef.close();
      });
    } else {
      this.save(settings);
    }
  }

  initAppRequirements() {
    this.shopService
      .getUrlToUpdateAppIfRequired(this.appRequiredScopes)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: { updateUrl: string; requireUpdate: boolean; missingScopes: string[] }) => {
        this.appRequirements = { updateRequired: res.requireUpdate, updateAppUrl: res.updateUrl };
        res.missingScopes?.forEach((scope) => {
          if (APP_REQUIREMENTS_SCOPE_TO_DISPLAY_TEXT_MAP[scope]) {
            this.appRequirementsList.push(APP_REQUIREMENTS_SCOPE_TO_DISPLAY_TEXT_MAP[scope]);
          }
        });
      });
  }

  onUpdateApp(): void {
    window.open(this.appRequirements?.updateAppUrl, '_blank');
  }
}
